export function isOnline(): boolean {
  return navigator.onLine;
}

export function handleNetworkError(err: any): string {
  if (!isOnline() || err?.code === "ERR_NAME_NOT_RESOLVED") {
    return "You appear to be offline. Check your connection and try again.";
  }

  if (
    err?.response?.status === 504 ||
    err?.code === "ECONNABORTED" ||
    err?.message?.includes("Request has been terminated")
  ) {
    return "This is taking longer than usual. Please try again.";
  }

  if (err?.response?.status === 502) {
    return "Service is temporarily unavailable. Please try again.";
  }

  if (err?.response?.status === 503) {
    return "Service is temporarily unavailable. Please try again.";
  }

  // AC6 – Rate limiting
  if (err?.response?.status === 429) {
    return "Too many attempts. Please wait a moment and try again.";
  }

  if (err?.response?.status === 403) {
    return "You don’t have permission to perform this action.";
  }

  return err;
}

export function sanitizeErrorMessage(err: unknown): unknown {
  // Handle null/undefined cases
  if (!err) {
    return "Something went wrong, please try again later.";
  }

  let message: string;

  // Extract message based on input type
  if (typeof err === "string") {
    message = err;
  } else if (err && typeof (err as any)?.message === "string") {
    message = (err as any).message;
  } else {
    // If it's an object without message or other non-string types, return as-is without sanitization
    return err;
  }

  // Convert to lowercase for safe comparison
  const safeMessage = message.toLowerCase();

  // Keywords that indicate a system-level / non-user-friendly error
  const errorIndicators = [
    "unexpected",
    "null",
    "object",
    "undefined",
    "exception",
    "invalid json",
    "status_code",
  ];

  // If message contains any unwanted system-like keywords → return friendly fallback
  const hasTechnicalTerm = errorIndicators.some((term) =>
    safeMessage.includes(term)
  );

  if (hasTechnicalTerm) {
    return "Something went wrong, please try again later.";
  }

  // If message looks normal and user readable → return original
  return message;
}
